import { useState } from 'react';

import type {
  AuditFinding,
  AuditFindingClassification,
  PlanAction,
  QualityItem,
  QualityItemKind,
  QualitySummary,
  ScheduleCadence,
  ScheduleOccurrence,
  ScheduleOccurrenceSkipReason,
} from '../domain-types';

export function useWorkflows() {
  const [activeKind, setActiveKind] = useState<QualityItemKind>('plan');
  const [qualityItems, setQualityItems] = useState<QualityItem[]>([]);
  const [qualitySummary, setQualitySummary] = useState<QualitySummary | null>(null);
  const [qualityHorizonDays, setQualityHorizonDays] = useState<30 | 60 | 90>(30);
  const [itemTitle, setItemTitle] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemDueAt, setItemDueAt] = useState('');
  const [planObligation, setPlanObligation] = useState('');
  const [planObjective, setPlanObjective] = useState('');
  const [planSuccessMeasures, setPlanSuccessMeasures] = useState('');
  const [scheduleActivity, setScheduleActivity] = useState('');
  const [scheduleCadence, setScheduleCadence] = useState<ScheduleCadence>('one_off');
  const [auditScope, setAuditScope] = useState('');
  const [auditCriteria, setAuditCriteria] = useState('');
  const [auditSampling, setAuditSampling] = useState('');
  const [completionSummaries, setCompletionSummaries] = useState<Record<string, string>>({});
  const [planApprovalComments, setPlanApprovalComments] = useState<Record<string, string>>({});
  const [planReopenReasons, setPlanReopenReasons] = useState<Record<string, string>>({});
  const [expandedPlanId, setExpandedPlanId] = useState('');
  const [planActions, setPlanActions] = useState<Record<string, PlanAction[]>>({});
  const [planActionDescription, setPlanActionDescription] = useState('');
  const [planActionDueAt, setPlanActionDueAt] = useState('');
  const [planActionEvidenceFileId, setPlanActionEvidenceFileId] = useState('');
  const [planActionCompletionSummaries, setPlanActionCompletionSummaries] = useState<
    Record<string, string>
  >({});
  const [expandedAuditId, setExpandedAuditId] = useState('');
  const [auditFindings, setAuditFindings] = useState<Record<string, AuditFinding[]>>({});
  const [auditFindingTitle, setAuditFindingTitle] = useState('');
  const [auditFindingStatement, setAuditFindingStatement] = useState('');
  const [auditFindingClassification, setAuditFindingClassification] =
    useState<AuditFindingClassification>('observation');
  const [auditFindingCorrectiveAction, setAuditFindingCorrectiveAction] = useState('');
  const [auditFindingDueAt, setAuditFindingDueAt] = useState('');
  const [auditFindingEvidenceFileId, setAuditFindingEvidenceFileId] = useState('');
  const [auditFindingClosureSummaries, setAuditFindingClosureSummaries] = useState<
    Record<string, string>
  >({});
  const [auditFindingClosureEvidence, setAuditFindingClosureEvidence] = useState<
    Record<string, string>
  >({});
  const [expandedScheduleId, setExpandedScheduleId] = useState('');
  const [scheduleOccurrences, setScheduleOccurrences] = useState<
    Record<string, ScheduleOccurrence[]>
  >({});
  const [scheduleOccurrenceView, setScheduleOccurrenceView] = useState<'agenda' | 'calendar'>(
    'agenda',
  );
  const [scheduleOccurrenceSummaries, setScheduleOccurrenceSummaries] = useState<
    Record<string, string>
  >({});
  const [scheduleOccurrenceReschedules, setScheduleOccurrenceReschedules] = useState<
    Record<string, { scheduledFor: string; reason: string }>
  >({});
  const [scheduleOccurrenceSkipReasons, setScheduleOccurrenceSkipReasons] = useState<
    Record<string, ScheduleOccurrenceSkipReason>
  >({});
  const [scheduleOccurrenceSkipDetails, setScheduleOccurrenceSkipDetails] = useState<
    Record<string, string>
  >({});
  const [savingItem, setSavingItem] = useState(false);

  return {
    activeKind,
    setActiveKind,
    qualityItems,
    setQualityItems,
    qualitySummary,
    setQualitySummary,
    qualityHorizonDays,
    setQualityHorizonDays,
    itemTitle,
    setItemTitle,
    itemDescription,
    setItemDescription,
    itemDueAt,
    setItemDueAt,
    planObligation,
    setPlanObligation,
    planObjective,
    setPlanObjective,
    planSuccessMeasures,
    setPlanSuccessMeasures,
    scheduleActivity,
    setScheduleActivity,
    scheduleCadence,
    setScheduleCadence,
    auditScope,
    setAuditScope,
    auditCriteria,
    setAuditCriteria,
    auditSampling,
    setAuditSampling,
    completionSummaries,
    setCompletionSummaries,
    planApprovalComments,
    setPlanApprovalComments,
    planReopenReasons,
    setPlanReopenReasons,
    expandedPlanId,
    setExpandedPlanId,
    planActions,
    setPlanActions,
    planActionDescription,
    setPlanActionDescription,
    planActionDueAt,
    setPlanActionDueAt,
    planActionEvidenceFileId,
    setPlanActionEvidenceFileId,
    planActionCompletionSummaries,
    setPlanActionCompletionSummaries,
    expandedAuditId,
    setExpandedAuditId,
    auditFindings,
    setAuditFindings,
    auditFindingTitle,
    setAuditFindingTitle,
    auditFindingStatement,
    setAuditFindingStatement,
    auditFindingClassification,
    setAuditFindingClassification,
    auditFindingCorrectiveAction,
    setAuditFindingCorrectiveAction,
    auditFindingDueAt,
    setAuditFindingDueAt,
    auditFindingEvidenceFileId,
    setAuditFindingEvidenceFileId,
    auditFindingClosureSummaries,
    setAuditFindingClosureSummaries,
    auditFindingClosureEvidence,
    setAuditFindingClosureEvidence,
    expandedScheduleId,
    setExpandedScheduleId,
    scheduleOccurrences,
    setScheduleOccurrences,
    scheduleOccurrenceView,
    setScheduleOccurrenceView,
    scheduleOccurrenceSummaries,
    setScheduleOccurrenceSummaries,
    scheduleOccurrenceReschedules,
    setScheduleOccurrenceReschedules,
    scheduleOccurrenceSkipReasons,
    setScheduleOccurrenceSkipReasons,
    scheduleOccurrenceSkipDetails,
    setScheduleOccurrenceSkipDetails,
    savingItem,
    setSavingItem,
  };
}
